/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** The offer, when somebody's seat is running out of room. */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Text, UnstyledButton } from '@/shared/ui';
import type { SeatUnlock, UnderPressure } from './seat-unlocks';

/** One thing the bigger seat gives that this one does not. */
const Gain: React.FC<{ label: string; from: number | null; to: number | null }> = ({
  label, from, to,
}) => (
  <div
    style={{
      display: 'flex', flexDirection: 'column', gap: 'var(--space-0-5)',
      padding: 'var(--space-3)', borderRadius: 'var(--radius-lg)',
      background: 'rgba(255, 255, 255, 0.08)', minWidth: 0,
    }}
  >
    <Text
      component="span"
      style={{
        // Wraps rather than truncates. The label carries the quota *and*
        // whose it is -- "Interações de IA/mês de Marcelo Barbosa" needs
        // 168px in a 142px card, and the ellipsis ate both halves of the
        // sentence at once. Two short lines say it; one clipped line does not.
        fontSize: 'var(--text-tag)', color: 'var(--brand-beige)',
        lineHeight: 1.35, textWrap: 'pretty',
      }}
    >
      {label}
    </Text>
    <Box style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
      <Text
        component="span"
        style={{
          fontFamily: 'var(--font-family-alt-2)', fontSize: 'var(--text-xl)',
          fontWeight: 'var(--font-bold)', color: 'var(--text-inverse)',
        }}
      >
        {to ?? '—'}
      </Text>
      <Text
        component="span"
        style={{
          fontSize: 'var(--text-meta)', color: 'var(--brand-beige)',
          textDecoration: 'line-through',
        }}
      >
        {from ?? '—'}
      </Text>
    </Box>
  </div>
);

export interface UpgradePushProps {
  pressure: UnderPressure;
  /** The plan above the one paying for that seat. */
  target: SeatUnlock;
  current: SeatUnlock;
  /** True when the target plan already has a seat nobody is using. */
  seatFreeOnTarget: boolean;
  onAct: () => void;
  onDismiss: () => void;
}

/**
 * The upgrade push.
 *
 * It names one person, because the thing being offered is one seat. The
 * agency's storage is deliberately absent: it is one shared pool and moving
 * a seat up does not enlarge it, so mentioning it would be selling something
 * the upgrade does not deliver.
 *
 * @param props - Who is short, and what the bigger seat gives them.
 * @returns The block.
 */
export const UpgradePush: React.FC<UpgradePushProps> = ({
  pressure, target, current, seatFreeOnTarget, onAct, onDismiss,
}) => {
  const { t } = useTranslation('features/subscription');
  const who = `${pressure.person.first_name ?? ''}`.trim() || pressure.person.email;
  const percent = Math.round(pressure.share * 100);

  return (
    <div
      data-testid="plan-upgrade-hot"
      style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center',
        justifyContent: 'space-between', gap: 'var(--space-5)',
        padding: 'var(--space-5) var(--space-6)',
        borderRadius: 'var(--radius-xl)', background: 'var(--seed-base)',
      }}
    >
      <div
        style={{
          display: 'flex', flexDirection: 'column', gap: 'var(--space-1-5)',
          flex: '1 1 22rem', minWidth: 0,
        }}
      >
        <Text
          component="span"
          style={{
            fontSize: 'var(--text-tag)', fontWeight: 'var(--font-bold)',
            letterSpacing: 'var(--tracking-chip)', textTransform: 'uppercase',
            color: 'var(--brand-beige)',
          }}
        >
          {t('v2.upgrade.kicker', { plan: current.title })}
        </Text>
        <Text
          component="span"
          style={{
            fontFamily: 'var(--font-family-alt-2)', fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-bold)', color: 'var(--text-inverse)',
            textWrap: 'pretty',
          }}
        >
          {t('v2.upgrade.title', {
            name: who,
            percent,
            quota: t(`v2.upgrade.quota.${pressure.quota}`),
          })}
        </Text>
        <Text
          style={{
            fontSize: 'var(--text-small)', lineHeight: 1.5,
            color: 'var(--brand-beige)', textWrap: 'pretty',
          }}
        >
          {seatFreeOnTarget
            ? t('v2.upgrade.moveCostsNothing', { plan: target.title })
            : t('v2.upgrade.proRataNote', { plan: target.title })}
        </Text>
      </div>

      <div
        style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 'var(--space-2)', flex: '1 1 18rem', minWidth: 0,
        }}
      >
        <Gain
          label={t('v2.upgrade.interactionsOf', { name: who })}
          from={current.interactions}
          to={target.interactions}
        />
        <Gain
          label={t('v2.upgrade.searchesOf', { name: who })}
          from={current.searches}
          to={target.searches}
        />
      </div>

      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
          marginLeft: 'auto',
        }}
      >
        <UnstyledButton
          onClick={onDismiss}
          style={{
            background: 'none', cursor: 'pointer', fontSize: 'var(--text-small)',
            fontWeight: 'var(--font-semibold)', color: 'var(--brand-beige)',
          }}
        >
          {t('v2.upgrade.notNow')}
        </UnstyledButton>
        <Button
          variant="solid"
          size="sm"
          onClick={onAct}
          style={{
            background: 'var(--surface-raised)', color: 'var(--seed-base)',
            borderRadius: 'var(--radius-full)', height: 40,
          }}
        >
          {seatFreeOnTarget
            ? t('v2.upgrade.moveThem', { name: who, plan: target.title })
            : t('v2.upgrade.upgradeThem', { name: who })}
        </Button>
      </div>
    </div>
  );
};
